/**
 * Shared Next-Task Assignment step. Every typed encounter form mounts the
 * same `NextTaskOwnerSection` JSX with the same `setFieldValue` wiring
 * to keep the form's `followup-assigned-to{,-display}` fields in sync
 * with the parent activity-completion override owner.
 *
 * @agentic connections
 */

import type { UseFormReturnType } from "@mantine/form";
import type { AppliedBusinessRule } from "@benchmark/core/resolvers";

import { NextTaskOwnerSection } from "@/components/shared/forms/NextTaskOwnerSection";

/**
 * Form shapes that own next-task assignment fields.
 *
 * @agentic connections
 */
type NextTaskOwnerCapableFormData = {
  "followup-assigned-to"?: string;
  "followup-assigned-to-display"?: string;
};

/**
 * Props for {@link NextTaskAssignmentStep}. Mirrors the prop bag the
 * typed forms previously passed to `NextTaskOwnerSection` plus a `form`
 * reference so the helper can sync the form fields itself.
 *
 * @agentic connections
 */
export type NextTaskAssignmentStepProps<
  T extends NextTaskOwnerCapableFormData,
> = {
  form: UseFormReturnType<T>;
  nextActivityLabel?: string;
  nextActivityDefaultOwner?: { ref: string; display: string };
  nextTaskOwnerOverride?: { ref: string; display: string } | null;
  onNextTaskOwnerOverrideChange?: (
    owner: { ref: string; display: string } | null,
  ) => void;
  nextActivityAppliedRule?: AppliedBusinessRule;
  isResolvingNextTaskOwner?: boolean;
};

/**
 * Renders the Assignment step for activity-driven encounter forms.
 * Synchronises the form's `followup-assigned-to{,-display}` fields with
 * the parent override owner whenever the user changes the assignment.
 *
 * @agentic connections
 */
export const NextTaskAssignmentStep = <T extends NextTaskOwnerCapableFormData>({
  form,
  nextActivityLabel,
  nextActivityDefaultOwner,
  nextTaskOwnerOverride,
  onNextTaskOwnerOverrideChange,
  nextActivityAppliedRule,
  isResolvingNextTaskOwner = false,
}: NextTaskAssignmentStepProps<T>) => {
  const handleChange = (owner: { ref: string; display: string } | null) => {
    onNextTaskOwnerOverrideChange?.(owner);
    form.setFieldValue(
      "followup-assigned-to" as never,
      (owner?.ref || "") as never,
    );
    form.setFieldValue(
      "followup-assigned-to-display" as never,
      (owner?.display || "") as never,
    );
  };

  return (
    <NextTaskOwnerSection
      nextActivityLabel={nextActivityLabel}
      defaultOwner={nextActivityDefaultOwner}
      appliedRule={nextActivityAppliedRule}
      overrideOwner={nextTaskOwnerOverride}
      onOverrideChange={handleChange}
      isResolving={isResolvingNextTaskOwner}
    />
  );
};
